import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { Calendar, Loader2, HelpCircle, Building, Users, UserPlus, LogIn } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingSystem, setCheckingSystem] = useState(true);
  const [isFirstSetup, setIsFirstSetup] = useState(false);
  const { login, settings } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    checkSystemStatus();
  }, []);

  const checkSystemStatus = async () => {
    try {
      const response = await axios.get(`${API}/system/status`);
      setIsFirstSetup(response.data.is_first_setup);
    } catch (error) {
      console.error("Failed to check system status");
    } finally {
      setCheckingSystem(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success("Accesso effettuato con successo!");
      
      // Redirect new titolari to settings for onboarding
      if (userData.ruolo === "titolare" && userData.onboarding_completato === false) {
        navigate("/settings?onboarding=true");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  // Help tooltip component
  const HelpTooltip = ({ content }) => (
    <Popover>
      <PopoverTrigger asChild>
        <button 
          type="button" 
          className="inline-flex items-center justify-center ml-2 h-5 w-5 rounded-full bg-theme-100 hover:bg-theme-200 text-theme-600 transition-colors"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" className="max-w-xs p-3 text-sm bg-slate-900 text-white border-slate-800">
        {content}
      </PopoverContent>
    </Popover>
  );

  if (checkingSystem) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // FIRST SETUP - No users exist
  if (isFirstSetup) {
    return (
      <div className="min-h-screen grid lg:grid-cols-2">
        {/* Left side - First Setup */}
        <div className="flex items-center justify-center p-4 sm:p-8 lg:p-12 pb-20 sm:pb-8 bg-white">
          <div className="w-full max-w-md space-y-6 sm:space-y-8 animate-fade-in">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Gestione Ferie</h2>
                <p className="text-sm text-slate-500">Configura la tua azienda</p>
              </div>
            </div>

            <div className="space-y-3">
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">Benvenuto</h1>
              <p className="text-slate-600">
                Registrati come titolare per creare un nuovo account aziendale
              </p>
            </div>

            {/* Onboarding Instructions */}
            <Card className="card-theme-highlight">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-theme-100 rounded-lg shrink-0">
                    <HelpCircle className="h-5 w-5 text-theme-600" />
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-semibold text-slate-900">Come iniziare</h3>
                    <ul className="text-sm text-slate-600 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="font-bold text-theme-600">1.</span>
                        <span>Registrati come <strong>Titolare</strong> per creare l'azienda</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold text-theme-600">2.</span>
                        <span>Configura le impostazioni aziendali (nome, logo, orari)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold text-theme-600">3.</span>
                        <span>I dipendenti potranno registrarsi successivamente</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={() => navigate("/register?role=titolare")}
              data-testid="first-setup-register-btn"
              className="w-full h-12 btn-theme rounded-md btn-press font-medium gap-2"
            >
              <Building className="h-5 w-5" />
              Registrati come Titolare
            </Button>
          </div>
        </div>

        {/* Right side - Image */}
        <div className="hidden lg:block relative">
          <img
            src="https://customer-assets.emergentagent.com/job_aea9fcf4-7192-4048-99b8-cf6b21f11caa/artifacts/1flrp8z8_Team.png"
            alt="Team working together"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/20"></div>
        </div>
      </div>
    );
  }

  // NORMAL LOGIN - Users exist
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-4 sm:p-8 lg:p-12 pb-20 sm:pb-8 bg-white">
        <div className="w-full max-w-md space-y-6 sm:space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="flex flex-col items-center text-center gap-4">
            <img 
              src="/logo_ferie_permessi.jpg" 
              alt="Ferie e Permessi" 
              className="h-24 w-auto object-contain rounded-lg shadow-md"
            />
            <div>
              <h2 className="text-xl font-bold text-slate-900" data-testid="company-name">Ferie e Permessi</h2>
              <p className="text-sm text-slate-500">Gestione Aziendale Ferie e Permessi</p>
              <p className="text-xs text-slate-400 mt-1">by LuiCas ™</p>
            </div>
          </div>

          <div className="space-y-2 text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Accedi</h1>
            <p className="text-slate-600">Inserisci le tue credenziali per accedere</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="nome@azienda.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              />
              <div className="text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-slate-500 hover:text-slate-900 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Password dimenticata?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className={`w-full h-12 text-white rounded-md btn-press font-medium ${
                settings?.tema_colore 
                  ? "btn-theme" 
                  : "bg-slate-700 hover:bg-slate-600"
              }`}
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Accesso in corso...</>
              ) : (
                "Accedi"
              )}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-500">Oppure</span>
            </div>
          </div>

          <div className="text-center space-y-3">
            <p className="text-slate-600">Non hai un account?</p>
            <Button
              variant="outline"
              onClick={() => navigate("/register")}
              data-testid="register-link"
              className={`w-full h-12 gap-2 text-sm sm:text-base ${
                settings?.tema_colore
                  ? "btn-theme-light"
                  : "btn-amber-light"
              }`}
            >
              <UserPlus className="h-5 w-5 shrink-0" />
              <span className="hidden sm:inline">Registrati come Titolare, Dipendente o Amministrativo</span>
              <span className="sm:hidden">Registrati</span>
            </Button>
            
            {/* Privacy Policy Link */}
            <p className="text-xs text-slate-400 mt-4">
              Accedendo, accetti la nostra{" "}
              <a href="/privacy-policy.html" target="_blank" className="text-slate-500 hover:text-slate-700 underline">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative">
        <img
          src="https://customer-assets.emergentagent.com/job_aea9fcf4-7192-4048-99b8-cf6b21f11caa/artifacts/1flrp8z8_Team.png"
          alt="Team working together"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/20"></div>
      </div>
    </div>
  );
}
